// The Fields dialog's and the outliner views' writes (the Mac's LibraryModel+Fields and +GridViews): each one of
// `PlanEditor`'s typed writes through the live project, returning why it was refused (a name taken, a journal status
// that stays) rather than throwing, so the dialog can say so. The view in use is this browser's choice, per project.
import type { Field, InkColor, MetadataValue, OpenedProject, Project } from '@writing-suite/core';
import { builtInGridViews, optionColor, readGridViews, type GridView, type StarterFieldSet } from '@writing-suite/core/plan';
import { planEdit } from './plan.ts';
import type { LiveProject } from './project.ts';

function refusal(fn: () => void): string | null {
  try {
    fn();
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}

// MARK: Fields

export const addField = (live: LiveProject, name: string, type: Field['type']) => refusal(() => planEdit(live, (e) => e.addField(name, type)));
export const renameField = (live: LiveProject, id: string, name: string) => refusal(() => planEdit(live, (e) => e.renameField(id, name)));
export const moveField = (live: LiveProject, id: string, after: string | null) => refusal(() => planEdit(live, (e) => e.moveField(id, after)));
export const setFieldHidden = (live: LiveProject, id: string, hidden: boolean) => refusal(() => planEdit(live, (e) => e.setFieldHidden(id, hidden)));
export const addStarterFields = (live: LiveProject, set: StarterFieldSet): string | null => {
  let added = 0;
  const refused = refusal(() => { added = planEdit(live, (e) => e.addStarterFields(set)).length; });
  return refused ?? (added ? null : `The project has every field in “${set.name}” already.`);
};

export const addOption = (live: LiveProject, fieldId: string, name: string) => refusal(() => planEdit(live, (e) => e.addOption(fieldId, name)));
export const renameOption = (live: LiveProject, fieldId: string, optionId: string, name: string) =>
  refusal(() => planEdit(live, (e) => e.renameOption(fieldId, optionId, name)));
export const recolorOption = (live: LiveProject, fieldId: string, optionId: string, ink: InkColor | null) =>
  refusal(() => planEdit(live, (e) => e.recolorOption(fieldId, optionId, ink ? optionColor(ink) : null)));
export const moveOption = (live: LiveProject, fieldId: string, optionId: string, index: number) =>
  refusal(() => planEdit(live, (e) => e.moveOption(fieldId, optionId, index)));

/** Takes an option out of a list; documents that have it move to `replacement` first (null: no value). */
export function removeOption(live: LiveProject, project: Project, fieldId: string, optionId: string, replacement: string | null): string | null {
  const using = documentsUsing(project, fieldId, optionId);
  const value: MetadataValue | null = replacement ? { type: 'text', value: replacement } : null;
  return refusal(() => planEdit(live, (e) => {
    if (using.length) e.setMetadataOn(using, fieldId, value);
    e.removeOption(fieldId, optionId);
  }));
}

/** Every document's id, for counting values (none while the project is opening). */
function fragmentIds(project: Project): string[] {
  const record = (project as OpenedProject).record;
  return record ? [...record.fragments.keys()] : [];
}

/** How many documents have a value in the field. */
export const documentsWithValue = (project: Project, fieldId: string) =>
  fragmentIds(project).filter((id) => project.fragment(id)?.metadata[fieldId] !== undefined).length;

/** The documents whose value is this option. */
export function documentsUsing(project: Project, fieldId: string, optionId: string): string[] {
  return fragmentIds(project).filter((id) => {
    const v = project.fragment(id)?.metadata[fieldId];
    return v?.type === 'text' && v.value === optionId;
  });
}

// MARK: Views

/** The built-in views, then the project's own by name. */
export function gridViewsFor(project: Project, showsScenes: boolean, journal = false): GridView[] {
  const record = (project as OpenedProject).record;
  return [...builtInGridViews(project.allFields(), showsScenes, journal), ...(record ? readGridViews(record.settings) : [])];
}

const BUILT_IN_NAMES = ['All columns', 'Structure', 'Progress', 'Story', 'Posts'];

export function saveGridView(live: LiveProject, view: Omit<GridView, 'id'>): { view: GridView | null; error: string | null } {
  let saved: GridView | null = null;
  const error = refusal(() => { saved = planEdit(live, (e) => e.addGridView(view, BUILT_IN_NAMES)); });
  return { view: saved, error };
}

export const updateGridView = (live: LiveProject, id: string, patch: Partial<Omit<GridView, 'id'>>) =>
  refusal(() => planEdit(live, (e) => e.updateGridView(id, patch, BUILT_IN_NAMES)));
export const deleteGridView = (live: LiveProject, view: GridView) => refusal(() => planEdit(live, (e) => e.deleteGridView(view)));
